import React, { CSSProperties, useState } from 'react';
import styled from 'styled-components';
import Select, {
  components,
  ActionMeta,
  ControlProps,
  OptionProps,
  PlaceholderProps,
} from 'react-select';
import { theme } from '../../../theme';
import { InputWrapperStyles, defaultInputHeight, InputStyles } from '../styles';
import { ButtonFocusStyles } from '../../button';
import { Label } from '../label';
import { BodyText } from '../../typography';
import { floatLabel } from '../helpers';
import { CaretIcon, InfoCircleIcon } from '../../../assets';
import { ErrorBox } from '../errorBox';
import { Tooltip } from '../../tooltip';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledSelect = styled(Select)`
  display: inline-block;
  width: 100%;
`;

const InputContainer = styled.div`
  width: 100%;
  ${InputWrapperStyles}
  align-items: center;
  cursor: pointer;
  height: ${defaultInputHeight};

  padding: ${theme.spacings[3].rem} ${theme.spacings[2].rem};

  &:active,
  &:focus,
  &:focus-within,
  &:focus-visible {
    ${ButtonFocusStyles};
  }
`;

const MenuWrapper = styled(components.Menu)<{
  isOpen: boolean;
}>`
  background-color: ${theme.colors.primary.white};
  height: fit-content;
  overflow-y: scroll;
  left: 0;
  position: absolute;
  width: 100%;
  transform: scaleY(0);
  transform-origin: top;
  transition: transform 0.2s;
  z-index: 999;
  background-color: ${theme.colors.primary.white};
  border: 1px solid ${theme.colors.neutrals.solidGrays[5]};
  border-top: none;
  box-sizing: border-box;
  ${({ isOpen }) => isOpen && `transform: scaleY(1)`}
`;

const StyledOptionWrapper = styled.div`
  > div {
    overflow-y: visible;
  }
`;

const StyledSingleValue = styled(components.SingleValue)`
  ${InputStyles}
`;

const StyledPlaceholderContainer = styled.div`
  top: 70%;
  position: absolute;
  width: 100%;
`;

const StyledDropdownIndicator = styled(components.DropdownIndicator)`
  position: absolute;
  top: 50%;
  width: 95%;
  height: 38px;

  > svg {
    position: absolute;
    right: 4%;
    transform: ${({ selectProps }) =>
      selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(360deg)'};
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

const StyledInfoCircleIcon = styled(InfoCircleIcon)`
  margin-left: ${({ theme }) => theme.spacings[1].rem};
  fill: ${({ theme }) => theme.colors.neutrals.text.tertiary};
`;

const Placeholder = (props: PlaceholderProps<any, false>) => {
  if (props.children === 'Select...') return null;
  return (
    <StyledPlaceholderContainer>
      <components.Placeholder {...props}>
        {props.children}
      </components.Placeholder>
    </StyledPlaceholderContainer>
  );
};

const Option = (props: OptionProps<any, false>) => (
  <components.Option {...props}>
    <BodyText>{props.children}</BodyText>
  </components.Option>
);

const Menu = (props: any) => (
  <MenuWrapper {...props} isOpen={!!props.selectProps.menuIsOpen}>
    <StyledOptionWrapper>{props.children}</StyledOptionWrapper>
  </MenuWrapper>
);

const selectStyles = (isAnimateOpen: boolean, maxDropDownHeight: number) => ({
  control: () => ({}),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  indicatorsContainer: () => ({}),
  option: (
    _: CSSProperties,
    { isFocused }: OptionProps<Record<string, unknown>, boolean>
  ) => ({
    cursor: 'pointer',
    padding: theme.spacings[3].rem,
    backgroundColor: isFocused ? theme.colors.neutrals.solidGrays[8] : 'unset',
    '&:not(:last-of-type)': {
      borderBottom: `1px solid ${theme.colors.neutrals.solidGrays[5]}`,
    },
  }),
  menu: () => ({
    position: 'absolute',
    width: '100%',
    maxHeight: isAnimateOpen ? maxDropDownHeight + 'px' : '0',
  }),
  valueContainer: () => ({
    width: '100%',
  }),
  singleValue: (provided: CSSProperties) => ({
    ...provided,
    marginLeft: 0,
    top: '70%',
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
  }),
});

const DropdownIndicator = (props: any) => {
  return (
    <StyledDropdownIndicator {...props}>
      <CaretIcon fill={theme.colors.primary.graysGray} width={20} />
    </StyledDropdownIndicator>
  );
};

export type FormDropDownOption = {
  label: string;
  value: string | number;
  icon?: HTMLElement;
};

export type FormDropDownProps = {
  disabled?: boolean;
  label?: string;
  onSelect: (option: string | number) => void;
  options: FormDropDownOption[];
  selectedOption: string | number;
  errorMessage?: string | JSX.Element;
  hasError?: boolean;
  isCompact?: boolean;
  required?: boolean;
  className?: string;
  menuPlacement?: string;
  placeholder?: string;
  hasTooltip?: boolean;
  toolTipData?: string;
  maxDropDownHeight?: number;
};

export const FormDropDown: React.FC<FormDropDownProps> = ({
  disabled = false,
  isCompact = false,
  required = false,
  className,
  label,
  onSelect,
  options,
  selectedOption,
  errorMessage,
  hasError,
  menuPlacement,
  placeholder,
  hasTooltip,
  toolTipData = 'Tooltip text',
  maxDropDownHeight = 500,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimateOpen, setIsAnimateOpen] = useState(false);

  const handleChange = (
    selectedOption: FormDropDownOption,
    { action }: ActionMeta<FormDropDownOption>
  ) => {
    if (action === 'select-option') {
      onSelect(selectedOption.value);
    }
  };

  const openMenu = () => {
    if (!isOpen) {
      setIsOpen(true);
      setIsAnimateOpen(true);
    }
  };

  const closeMenu = () => {
    if (isOpen) {
      setIsAnimateOpen(false);
      setTimeout(() => {
        setIsOpen(false);
      }, 200);
    }
  };

  const SingleValue = (props: any) => (
    <StyledSingleValue {...props} isCompact={isCompact} showPlaceholder={false}>
      {props.children}
    </StyledSingleValue>
  );

  const Control = (props: ControlProps<any, false>) => (
    <>
      <components.Control {...props}>
        {label && (
          <Label
            float={floatLabel(isCompact, props.hasValue, false)}
            isCompact={isCompact}
            required={required}
            value={label}
          />
        )}

        <InputContainer
          disabled={props.isDisabled}
          hasError={!!errorMessage || !!hasError}
          readOnly={false}
          isFocussed={props.isFocused}
        >
          {props.children}
        </InputContainer>
      </components.Control>
      {hasTooltip && (
        <IconWrapper>
          <Tooltip
            customTrigger={<StyledInfoCircleIcon width={24} />}
            text={toolTipData}
          ></Tooltip>
        </IconWrapper>
      )}
    </>
  );

  return (
    <Wrapper className={className}>
      <StyledSelect
        menuPlacement={menuPlacement}
        menuIsOpen={isOpen}
        onMenuClose={closeMenu}
        onMenuOpen={openMenu}
        isDisabled={disabled}
        isSearchable={false}
        onChange={handleChange}
        options={options}
        styles={selectStyles(isAnimateOpen, maxDropDownHeight)}
        value={options.filter((option) => option.value === selectedOption)}
        placeholder={placeholder}
        components={{
          Control,
          Placeholder,
          Option,
          Menu,
          SingleValue,
          DropdownIndicator,
        }}
      />
      {errorMessage && <ErrorBox size="sm">{errorMessage}</ErrorBox>}
    </Wrapper>
  );
};
